import { useCallback, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { isSuperAdminEmail } from "../../shared/admin";
import Shell from "./components/Shell";
import { clearSession, readSession } from "./lib/kv";
import {
  askDisconnectFlow,
  endImpersonation,
  readImpersonation,
  type ImpersonationMeta,
} from "./lib/impersonation";
import Admin from "./pages/Admin";
import Kit from "./pages/Kit";
import Login from "./pages/Login";
import RoutesPage from "./pages/Routes";
import SearchActivities from "./pages/SearchActivities";
import SurprisePlan from "./pages/SurprisePlan";

type SessionUser = { id: string; email: string; name: string; token: string };

function AppRoutes() {
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState<SessionUser | null>(null);
  const [impersonation, setImpersonation] = useState<ImpersonationMeta | null>(null);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    const session = await readSession();
    setUser(session);
    setImpersonation(readImpersonation());
    setReady(true);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const disconnect = useCallback(async () => {
    const choice = await askDisconnectFlow();
    if (choice === "cancel") return;
    try {
      await endImpersonation(choice === "save" || choice === "live");
      await refresh();
      navigate("/admin");
    } catch (err) {
      window.alert(err instanceof Error ? err.message : "No se pudo desconectar");
    }
  }, [navigate, refresh]);

  if (!ready) {
    return (
      <div className="boot-screen">
        <p className="login-brand">HERMES</p>
        <p>Encendiendo el mapa…</p>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={
          user ? (
            <Navigate to="/" replace />
          ) : (
            <Login onAuthed={() => void refresh()} />
          )
        }
      />
      <Route
        element={
          user ? (
            <Shell
              userName={user.name || user.email}
              loggedIn
              isAdmin={isSuperAdminEmail(user.email) && !impersonation}
              impersonation={impersonation}
              onDisconnect={() => void disconnect()}
              onLogout={() => {
                void clearSession().then(() => setUser(null));
              }}
            />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      >
        <Route path="/" element={<SearchActivities />} />
        <Route path="/surprise" element={<SurprisePlan />} />
        <Route path="/routes" element={<RoutesPage />} />
        <Route path="/perfil" element={<Kit />} />
        <Route path="/kit" element={<Kit />} />
        <Route path="/admin" element={<Admin />} />
      </Route>
      <Route path="*" element={<Navigate to={user ? "/" : "/login"} replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter basename="/hermes/web">
      <AppRoutes />
    </BrowserRouter>
  );
}
